import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ArrowRight, TrendingUp, PieChart, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
}

const Confetti = () => {
  const confetti = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 3,
    size: 4 + Math.random() * 8,
    color: ['#16c47f', '#14b371', '#fbbf24', '#60a5fa', '#f472b6', '#a78bfa'][
      Math.floor(Math.random() * 6)
    ],
  }));

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-50">
      {confetti.map((c) => (
        <motion.div
          key={c.id}
          className="absolute rounded-full"
          style={{
            left: `${c.x}%`,
            width: c.size,
            height: c.size,
            backgroundColor: c.color,
          }}
          initial={{ y: -20, opacity: 1, rotate: 0 }}
          animate={{
            y: '100vh',
            opacity: [1, 1, 0],
            rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
          }}
          transition={{
            duration: c.duration,
            delay: c.delay,
            ease: 'easeIn',
          }}
        />
      ))}
    </div>
  );
};

export default function WelcomeModal({ isOpen, onClose, userName }: WelcomeModalProps) {
  const { language } = useTranslation();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const features = [
    {
      icon: TrendingUp,
      title: language === 'fr' ? 'Suivi en temps réel' : 'Real-time tracking',
      desc: language === 'fr' ? 'Visualisez vos investissements' : 'Visualize your investments',
    },
    {
      icon: PieChart,
      title: language === 'fr' ? 'Analyse détaillée' : 'Detailed analysis',
      desc: language === 'fr' ? 'Comprenez votre patrimoine' : 'Understand your wealth',
    },
    {
      icon: Shield,
      title: language === 'fr' ? 'Sécurité maximale' : 'Maximum security',
      desc: language === 'fr' ? 'Vos données sont protégées' : 'Your data is protected',
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {showConfetti && <Confetti />}

          <motion.div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={handleClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="relative bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-3xl p-8 max-w-md w-full shadow-xl z-10"
            initial={{ scale: 0.8, y: 40, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.8, y: 40, opacity: 0 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          >
            <div className="text-center mb-8">
              <motion.div
                className="w-20 h-20 bg-[#16c47f] rounded-2xl flex items-center justify-center mx-auto mb-6"
                initial={{ rotate: -180, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: 'spring', delay: 0.2, damping: 15 }}
              >
                <CheckCircle2 className="w-10 h-10 text-white" />
              </motion.div>

              <motion.h2
                className="text-3xl font-bold text-gray-900 dark:text-white mb-3"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                {language === 'fr' ? `Bienvenue ${userName} ! 🎉` : `Welcome ${userName}! 🎉`}
              </motion.h2>

              <motion.p
                className="text-gray-500 dark:text-gray-400"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4 }}
              >
                {language === 'fr'
                  ? 'Votre compte est prêt. Découvrez tout ce que clevia peut faire pour vous.'
                  : 'Your account is ready. Discover everything clevia can do for you.'}
              </motion.p>
            </div>

            <motion.div
              className="space-y-3 mb-8"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl p-4"
                >
                  <div className="w-10 h-10 bg-[#16c47f]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-[#16c47f]" />
                  </div>
                  <div>
                    <p className="text-gray-900 dark:text-white font-medium text-sm">
                      {feature.title}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              <Button
                onClick={handleClose}
                className="w-full h-14 bg-[#16c47f] hover:bg-[#14b371] text-white font-semibold rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.02]"
              >
                {language === 'fr' ? 'Commencer' : 'Get started'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
